import { useRouter } from 'expo-router'
import { useEffect } from 'react'
import { Image, Platform, ScrollView, StyleSheet, View } from 'react-native'
import { Card, Divider, Icon, Text, useTheme } from 'react-native-paper'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useAuth } from '@/lib/auth'
import { ButtonLink, Link } from '@/ui/Link'
import { LoadingIndicator } from '@/ui/LoadingIndicator'

export default function HomePage() {
    const { user, isLoggedIn, isInitializing } = useAuth({ throwIfAnon: false })
    const router = useRouter()
    const theme = useTheme()

    useEffect(() => {
        if (isLoggedIn && user?.primaryOrgSlug) {
            router.replace(`/${user.primaryOrgSlug}`)
        }
    }, [isLoggedIn, user?.primaryOrgSlug, router])

    if (isInitializing || (isLoggedIn && user?.primaryOrgSlug)) {
        return <LoadingIndicator />
    }

    const ContentWrapper = Platform.OS === 'ios' ? SafeAreaView : View

    return (
        <ContentWrapper
            style={[styles.safeContainer, { backgroundColor: theme.colors.background }]}
        >
            <ScrollView contentContainerStyle={styles.scrollContainer}>
                <View style={styles.container}>
                    <View style={styles.heroSection}>
                        <View style={styles.logoContainer}>
                            <Image
                                source={require('@/assets/images/icon.png')}
                                style={styles.logoImage}
                            />
                            <Text
                                variant="displaySmall"
                                style={[styles.logoText, { color: theme.colors.onBackground }]}
                            >
                                JobTime
                            </Text>
                        </View>

                        <Text
                            variant="bodyLarge"
                            style={[styles.tagline, { color: theme.colors.onSurfaceVariant }]}
                        >
                            Track your work time automatically
                        </Text>
                    </View>

                    <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
                        <Card.Content style={styles.cardContent}>
                            <View style={styles.cardHeader}>
                                <Icon
                                    source="account-check"
                                    size={28}
                                    color={theme.colors.primary}
                                />
                                <Text
                                    variant="titleLarge"
                                    style={[styles.cardTitle, { color: theme.colors.onSurface }]}
                                >
                                    Already have an account?
                                </Text>
                            </View>
                            <ButtonLink
                                href="/auth/sign-in"
                                title="Sign In"
                                mode="contained"
                                style={styles.button}
                            />
                        </Card.Content>
                    </Card>

                    <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
                        <Card.Content style={styles.cardContent}>
                            <View style={styles.cardHeader}>
                                <Icon
                                    source="email-outline"
                                    size={28}
                                    color={theme.colors.primary}
                                />
                                <Text
                                    variant="titleLarge"
                                    style={[styles.cardTitle, { color: theme.colors.onSurface }]}
                                >
                                    Invited by your organization?
                                </Text>
                            </View>
                            <Text
                                variant="bodyMedium"
                                style={[
                                    styles.cardDescription,
                                    { color: theme.colors.onSurfaceVariant },
                                ]}
                            >
                                Check your email for an invite link from your organization. Click
                                that link to create your account and join your team.
                            </Text>
                            <Divider
                                style={[
                                    styles.cardDivider,
                                    { backgroundColor: theme.colors.outlineVariant },
                                ]}
                            />
                            <Text
                                variant="bodySmall"
                                style={[styles.helpText, { color: theme.colors.onSurfaceVariant }]}
                            >
                                No invite yet? Ask your organization to send one to your email
                                address.
                            </Text>
                        </Card.Content>
                    </Card>

                    <View style={styles.footer}>
                        <Text
                            variant="bodySmall"
                            style={[styles.footerText, { color: theme.colors.onSurfaceVariant }]}
                        >
                            Are you a business owner?
                        </Text>
                        <Link href="/auth/sign-up">
                            <Text
                                variant="bodySmall"
                                style={[styles.footerLink, { color: theme.colors.primary }]}
                            >
                                Create an organization
                            </Text>
                        </Link>
                    </View>
                </View>
            </ScrollView>
        </ContentWrapper>
    )
}

const styles = StyleSheet.create({
    safeContainer: {
        flex: 1,
    },
    scrollContainer: {
        flexGrow: 1,
    },
    container: {
        flex: 1,
        padding: 20,
        gap: 24,
        maxWidth: 500,
        width: '100%',
        alignSelf: 'center',
    },
    heroSection: {
        alignItems: 'center',
        gap: 12,
        paddingVertical: 32,
    },
    logoContainer: {
        alignItems: 'center',
        gap: 12,
    },
    logoImage: {
        width: 80,
        height: 80,
        borderRadius: 20,
    },
    logoText: {
        fontWeight: '700',
    },
    tagline: {
        textAlign: 'center',
    },
    card: {
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
    },
    cardContent: {
        gap: 16,
        padding: 20,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    cardTitle: {
        fontWeight: '600',
        flex: 1,
    },
    cardDescription: {
        lineHeight: 22,
    },
    cardDivider: {
        height: 1,
    },
    helpText: {
        fontStyle: 'italic',
    },
    button: {
        marginTop: 4,
    },
    footer: {
        alignItems: 'center',
        paddingVertical: 24,
        gap: 8,
    },
    footerText: {
        textAlign: 'center',
    },
    footerLink: {
        textAlign: 'center',
        textDecorationLine: 'underline',
    },
})
